<template>
  <VeeForm ref="ibanForm" v-slot="{ pristine }" class="iban-form-component" @submit="onSave">
    <ExtraTipbox class="text-left q-mb-lg">
      <h4>
        {{ tipboxText }}
      </h4>
    </ExtraTipbox>
    <template v-if="readonly">
      <QInput :model-value="ibanDetails.getOwner()" readonly class="q-mb-md" type="text" :label="$t('ETAB.PAGES.PAYMENT.ETAB_IBAN.@OWNER')" />
      <QInput :model-value="ibanDetails.getIbanLastDigits()" readonly class="q-mb-md" type="text" :label="$t('ETAB.PAGES.PAYMENT.ETAB_IBAN.@IBAN')" />
    </template>
    <template v-else>
      <Field
        ref="owner"
        v-slot="{ errors, field, value }"
        v-model.trim="internalIban[IIban.owner]"
        as="div"
        :name="$filters.formLabelValidation($t('ETAB.PAGES.PAYMENT.ETAB_IBAN.@OWNER'))"
        :rules="{ required: true }"
      >
        <QInput
          v-bind="field"
          :model-value="value"
          :readonly="readonly"
          class="q-mb-md"
          type="text"
          :label="$t('ETAB.PAGES.PAYMENT.ETAB_IBAN.@OWNER')"
          :error="errors.length > 0"
          :error-message="errors[0]"
        />
      </Field>
      <StripeElements
        :ref="STRIPE_FIELDS.GROUP"
        v-slot="{ elements }"
        :stripe-key="stripeKey"
        :instance-options="instanceOptions"
        :elements-options="elementsOptions"
      >
        <QField
          stack-label
          :label="$t('ETAB.PAGES.PAYMENT.ETAB_IBAN.@IBAN')"
          :error-message="getError(STRIPE_FIELDS.IBAN)"
          :error="hasError(STRIPE_FIELDS.IBAN)"
        >
          <template #control>
            <StripeElement
              :ref="STRIPE_FIELDS.IBAN"
              type="iban"
              :elements="elements"
              :options="ibanOptions"
              @change="onChange"
              @ready="getAndEmitStripeInstances"
            />
          </template>
        </QField>
      </StripeElements>
    </template>

    <div v-if="!readonly" class="full-width row justify-center q-mb-lg q-gutter-md">
      <QBtn
        class="full-width btn-navigation"
        color="secondary"
        :label="$t('ETAB.PAGES.PAYMENT.ETAB_IBAN.@ADD_IBAN_BTN')"
        no-caps
        type="submit"
        :disable="!isIbanValid || pristine"
      />
    </div>
  </VeeForm>
</template>

<script>
  import { Form as VeeForm, Field } from "vee-validate";
  import { StripeElements, StripeElement } from "vue-stripe-js";
  import CoreColors from "@core/styles/core.variables.json";
  import { STRIPE_PK, STRIPE_FIELDS } from "@core/constants";
  import { IbanModel, IIban } from "@core/models";

  export default {
    components: { VeeForm, Field, StripeElements, StripeElement },
    props: {
      readonly: {
        type: Boolean,
        default: false,
      },
      ibanDetails: {
        type: Object,
        default: () => new IbanModel(),
      },
    },
    emits: ["saveIban", "referenceStripeInstances"],
    data() {
      return {
        internalIban: new IbanModel(),
        isIbanValid: false,
        stripeKey: STRIPE_PK,
        fields: {
          [STRIPE_FIELDS.IBAN]: {},
        },
        instanceOptions: {
          // https://stripe.com/docs/js/initializing#init_stripe_js-options
          locale: "fr",
        },
        elementsOptions: {
          // https://stripe.com/docs/js/elements_object/create#stripe_elements-options
        },
        stripeOptions: {
          style: {
            base: { fontFamily: "Arial" },
            invalid: {
              color: CoreColors.negative,
              ":focus": {
                color: CoreColors.negative,
              },
            },
          },
        },
      };
    },
    computed: {
      tipboxText() {
        return this.readonly ? this.$t("ETAB.PAGES.PAYMENT.ETAB_IBAN.@UPDATE_IBAN_TIP") : this.$t("ETAB.PAGES.PAYMENT.ETAB_IBAN.@ADD_IBAN_TIP");
      },
    },
    created() {
      this.STRIPE_FIELDS = STRIPE_FIELDS;
      this.IIban = IIban;
      if (!this.readonly) {
        this.initStripeOptions();
      }
    },
    unmounted() {
      if (!this.readonly) {
        document.querySelectorAll("iframe").forEach((iframe) => {
          if (iframe.getAttribute("src")?.includes("stripe")) {
            iframe.parentNode.removeChild(iframe);
          }
        });
      }
    },
    methods: {
      initStripeOptions() {
        this.ibanOptions = { ...this.fieldsOptions, supportedCountries: ["SEPA"], placeholderCountry: "FR" };
      },
      getAndEmitStripeInstances() {
        // we need to send Stripe instance and Stripe Iban element
        // careful, the Stripe Iban element is available after a little delay
        // (hence the @ready method)
        // Stripe instance is hidden in the StripeElements component
        // https://github.com/ectoflow/vue-stripe-elements#3-use-built-in-components
        const stripeInstance = this.$refs[STRIPE_FIELDS.GROUP]?.instance;
        // Stripe CardNumber element is in here as well
        const ibanStripeElement = this.$refs[STRIPE_FIELDS.IBAN]?.stripeElement;
        this.$emit("referenceStripeInstances", { stripeInstance, ibanStripeElement });
      },
      getError(type) {
        return this.fields[type].error?.message;
      },
      hasError(type) {
        return this.fields[type].error !== undefined;
      },
      isValid(type) {
        return this.fields[type].complete === true;
      },
      onChange(event) {
        if (event) {
          const type = event.elementType;
          // we save the event
          this.fields[type] = event;
          // if iban is valid, update isIbanValid state
          this.isIbanValid = this.isValid(type);
        }
      },
      async onSave() {
        this.$emit("saveIban", { formData: this.internalIban });
      },
    },
  };
</script>

<style lang="scss">
  .iban-form-component {
    .StripeElement {
      min-width: 280px; // for some reason the width is 0 by default because Stripe elements thinks it's empty
    }

    .q-field {
      margin-bottom: 25px;
    }
  }
</style>
